import { useState, useEffect, Key } from 'react';
import { Grid, CircularProgress, Pagination } from '@mui/material';
import Sidebar from './elements/Sidebar';
import ChildrenTabs from './children/ChildrenTabs';
import { useUser } from '../contexts/UserContext';
import useEventFetching from '../hooks/events/useEventFetching';
import useEventGroups from '../hooks/events/useEventGroups';
import SelectSidebar from './elements/SelectSidebar';
import { getGroupId } from '../utils';
import { ALL_EVENTS_GROUP } from '../constants';
import SearchBar from './elements/SearchBar';
import CartDrawer from './cart/CartDrawer';
import Banner from './elements/Banner';
import EventsCard from './events/Eventscard';
import { useNavigate } from 'react-router-dom';
import { Child } from '../types';
import { useDebounce } from '../hooks/useDebounce';

const MainLayout = () => {
    const { user, selectedChild, setSelectedChild } = useUser();
    const [filteredEventGroups, setFilteredEventGroups] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEventGroup, setSelectedEventGroup] = useState<any>(null);
    const { eventGroups, loading: eventGroupsLoading } = useEventGroups();
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    // Reset current page when selected event group changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedEventGroup]);

    // Set the initial event group and child when the page is loaded
    useEffect(() => {
        setCurrentPage(1);
        setFilteredEventGroups(eventGroups);
        const groupIdFromUrl = getGroupId();
        if (eventGroups.length > 0) {
            const selectedGroup = eventGroups.find(group => group.id == groupIdFromUrl);
            if (selectedGroup) {
                setSelectedEventGroup(selectedGroup);
            } else {
                setSelectedEventGroup(ALL_EVENTS_GROUP);
            }
        }
    }, [selectedChild, eventGroups, setSelectedEventGroup]);

    // Reset the current page when the user is not logged in
    useEffect(() => {
        if (!user) {
            setCurrentPage(1);
            setSelectedEventGroup(ALL_EVENTS_GROUP);
            setSelectedChild(null);
            setSearchQuery(null);
        }
    }, [user]);

    // Reset the current page when the search query changes
    useEffect(() => {
        if (debouncedSearchQuery !== null) {
            setCurrentPage(1);
        }
    }, [debouncedSearchQuery]);

    const handleSearchChange = (newQuery: string) => {
        setSearchQuery(newQuery);
    };

    const handleChildTabChange = (child: Child) => {
        setSelectedChild(child);
        // Clear the group id from the url
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('group_id');
        navigate(`?${searchParams.toString()}`, { replace: true });
    };

    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

    const navigate = useNavigate();

    const handleEventClick = (event: any) => {
        navigate(`/event/${event.id}`, { state: { event } });
    };

    const { loading: eventFetchingLoading, filteredEvents, totalPages } = useEventFetching({
        selectedEventGroup,
        selectedChild,
        currentPage,
        searchQuery: debouncedSearchQuery,
        user
    });

    const handleGroupChange = (group: any) => {
        setSelectedEventGroup(group);
        // Update URL with new group ID
        const searchParams = new URLSearchParams(window.location.search);
        if (group.id === ALL_EVENTS_GROUP.id) {
            searchParams.delete('group_id');
        } else {
            searchParams.set('group_id', group.id);
        }
        navigate(`?${searchParams.toString()}`, { replace: true });
    };

    return (
        <>
            <div className="hide-md pb-2">
                <Banner eventGroupImage={selectedEventGroup?.signedUrl || null} />
            </div>
            <div className="event-page p-5 pt-xs-0 mb-20">
                <Grid container spacing={2} className="pt-2 pt-xs-0 events-main-page">
                    <CartDrawer open={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />
                    <Grid item xs={12} md={0.5} lg={1}></Grid>
                    <Grid item xs={12} md={11} lg={10}>
                        <Grid item xs={12} md={12} className="pb-5 pt-5 hide-mobile">
                            <Banner eventGroupImage={selectedEventGroup?.signedUrl || null} />
                        </Grid>
                        <Grid item xs={12} className="hide-md">
                            <Grid item xs={12}>
                                <SelectSidebar
                                    eventGroups={filteredEventGroups}
                                    setSelectedEventGroup={handleGroupChange}
                                    selectedEventGroup={selectedEventGroup}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={12} className="pb-5">
                            <h1 className='event-group-title pb-0'>
                                {filteredEventGroups.length > 0 && selectedEventGroup ? selectedEventGroup.name : '\u00A0'}
                            </h1>
                            <h4 className='event-group-location-title pb-2'>
                                {filteredEventGroups.length > 0 && selectedEventGroup ? selectedEventGroup.locationName : '\u00A0'}
                            </h4>
                            <div className="pb-5">
                                {filteredEventGroups.length > 0 && selectedEventGroup ? selectedEventGroup.description : '\u00A0'}
                            </div>
                            <hr></hr>
                        </Grid>
                        {user &&
                            <Grid item xs={12} md={12} className="pb-5">
                                <ChildrenTabs onTabChange={(child: Child) => handleChildTabChange(child)} />
                            </Grid>
                        }
                        <Grid item xs={12} md={12}>
                            <div className="">
                                <div className="flex flex-row">
                                    <Grid item xs={12} md={1.5} className="hide-mobile" style={{maxHeight: '1200px', overflowY: 'auto'}}>
                                        <Sidebar
                                            eventGroups={eventGroups}
                                            setSelectedEventGroup={handleGroupChange}
                                            selectedEventGroup={selectedEventGroup}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={0.25} className="hide-mobile"></Grid>
                                    <Grid item xs={12} md={12} lg={12} xl={10.25}>
                                        <Grid item xs={12} md={12} className="pb-5">
                                            <SearchBar onSearchChange={handleSearchChange} />
                                        </Grid>
                                        {eventGroupsLoading || eventFetchingLoading ? (
                                            <div className="text-center mt-20">
                                                <CircularProgress />
                                            </div>
                                        ) : (
                                            <Grid container spacing={3.5}>
                                                {selectedEventGroup && filteredEvents.length > 0 ? filteredEvents.map((event: { id: Key | null | undefined; }) => (
                                                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                                                        <EventsCard event={event} onRegisterClick={() => handleEventClick(event)} />
                                                    </Grid>
                                                )) : (
                                                    <Grid item xs={12}>
                                                        <p className="p-info bigger mt-10">
                                                            {selectedEventGroup && selectedChild ? "There are no " + selectedEventGroup.name + " events available for this child's age." : "No events available."}
                                                        </p>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}
                                        {!(eventGroupsLoading || eventFetchingLoading) && filteredEvents.length > 0 &&
                                            <div className="pagination-events">
                                                <Pagination
                                                    count={totalPages}
                                                    page={currentPage}
                                                    onChange={(_event, page) => setCurrentPage(page)}
                                                    color="primary"
                                                />
                                            </div>
                                        }
                                    </Grid>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

export default MainLayout;
